import { Fragment, useEffect, useMemo, useState } from "react";

import {
  api,
  type GradedGame,
  type ModelPerformance,
  type ResultsResponse,
  type ResultsWindow,
} from "../lib/api";
import { useAuth } from "../lib/auth";

const MODEL_COLORS: Record<string, string> = {
  baseline: "#94a3b8",
  dvoa_box_impact: "#22d3ee",
  epm_style: "#f5a623",
  user: "#818cf8",
};

function modelColor(id: string): string {
  return MODEL_COLORS[id] ?? "#94a3b8";
}

function fmtPct(p: number | null | undefined, dp = 1): string {
  if (p == null || Number.isNaN(p)) return "—";
  return `${(p * 100).toFixed(dp)}%`;
}

function fmtNum(n: number | null | undefined, dp = 3): string {
  if (n == null || Number.isNaN(n)) return "—";
  return n.toFixed(dp);
}

function fmtROI(roi: number | null | undefined): string {
  if (roi == null || Number.isNaN(roi)) return "—";
  return `${roi >= 0 ? "+" : ""}${(roi * 100).toFixed(1)}%`;
}

function fmtRecord(rec: [number, number, number]): string {
  const [w, l, p] = rec;
  return p > 0 ? `${w}–${l}–${p}` : `${w}–${l}`;
}

function fmtGameDate(raw: string): string {
  if (!raw) return "—";
  const d = new Date(`${raw}T00:00:00`);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function Sparkline({
  data,
  color,
  width = 80,
  height = 26,
}: {
  data: number[];
  color: string;
  width?: number;
  height?: number;
}) {
  if (data.length < 2) {
    return <span className="muted small">—</span>;
  }
  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = max - min || 1;
  const stepX = width / (data.length - 1);
  const points = data
    .map(
      (v, i) =>
        `${(i * stepX).toFixed(1)},${(height - ((v - min) / span) * height).toFixed(1)}`,
    )
    .join(" ");
  return (
    <svg width={width} height={height} aria-hidden focusable="false">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

type SeriesKey = keyof ModelPerformance["series"];

type ChartSpec = {
  key: SeriesKey;
  label: string;
  dir: "lower" | "higher";
  fmt: "num" | "pct";
};

const CHART_SPECS: readonly ChartSpec[] = [
  {
    key: "cum_log_loss",
    label: "Cumulative Log Loss",
    dir: "lower",
    fmt: "num",
  },
  { key: "cum_brier", label: "Cumulative Brier", dir: "lower", fmt: "num" },
  {
    key: "cum_pick_acc",
    label: "Cumulative Pick Acc",
    dir: "higher",
    fmt: "pct",
  },
  { key: "cum_roi", label: "Cumulative ROI", dir: "higher", fmt: "pct" },
];

function MultiLineChart({
  models,
  spec,
  width = 460,
  height = 180,
}: {
  models: ModelPerformance[];
  spec: ChartSpec;
  width?: number;
  height?: number;
}) {
  const padL = 36;
  const padR = 12;
  const padT = 10;
  const padB = 22;
  const innerW = width - padL - padR;
  const innerH = height - padT - padB;
  const allVals: number[] = [];
  let maxLen = 0;
  for (const m of models) {
    const s = m.series[spec.key] ?? [];
    maxLen = Math.max(maxLen, s.length);
    for (const v of s) allVals.push(v);
  }
  if (!allVals.length || maxLen < 2) {
    return (
      <div className="results-chart-empty" style={{ height }}>
        <span className="muted small">Not enough data</span>
      </div>
    );
  }
  let min = Math.min(...allVals);
  let max = Math.max(...allVals);
  if (spec.key === "cum_roi") {
    min = Math.min(min, 0);
    max = Math.max(max, 0);
  }
  const span = max - min || 1;
  const padSpan = span * 0.08;
  const yMin = min - padSpan;
  const yMax = max + padSpan;
  const yRange = yMax - yMin || 1;

  const xOf = (i: number, n: number) =>
    n <= 1 ? padL : padL + (i / (n - 1)) * innerW;
  const yOf = (v: number) => padT + (1 - (v - yMin) / yRange) * innerH;

  const ticks = [yMax, (yMax + yMin) / 2, yMin];
  const fmt = (v: number) =>
    spec.fmt === "pct" ? `${(v * 100).toFixed(0)}%` : v.toFixed(2);

  return (
    <svg
      width={width}
      height={height}
      role="img"
      aria-label={spec.label}
      className="results-chart-svg"
    >
      {ticks.map((t, i) => (
        <g key={i}>
          <line
            x1={padL}
            x2={padL + innerW}
            y1={yOf(t)}
            y2={yOf(t)}
            stroke="#2a2c3f"
            strokeDasharray="2 4"
          />
          <text
            x={padL - 4}
            y={yOf(t) + 3}
            fontSize="9"
            textAnchor="end"
            fill="#6b7280"
          >
            {fmt(t)}
          </text>
        </g>
      ))}
      {spec.key === "cum_roi" && yMin < 0 && yMax > 0 && (
        <line
          x1={padL}
          x2={padL + innerW}
          y1={yOf(0)}
          y2={yOf(0)}
          stroke="#4a4c68"
          strokeWidth={1}
        />
      )}
      {models.map((m) => {
        const data = m.series[spec.key] ?? [];
        if (data.length < 2) return null;
        const pts = data
          .map(
            (v, i) => `${xOf(i, data.length).toFixed(1)},${yOf(v).toFixed(1)}`,
          )
          .join(" ");
        return (
          <polyline
            key={m.id}
            points={pts}
            fill="none"
            stroke={modelColor(m.id)}
            strokeWidth={1.75}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        );
      })}
    </svg>
  );
}

function CalibrationChart({
  models,
  width = 460,
  height = 220,
}: {
  models: ModelPerformance[];
  width?: number;
  height?: number;
}) {
  const pad = 28;
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;
  const x = (p: number) => pad + p * innerW;
  const y = (p: number) => pad + (1 - p) * innerH;
  const hasAny = models.some((m) => (m.calibration?.length ?? 0) > 0);
  if (!hasAny) {
    return (
      <div className="results-chart-empty" style={{ height }}>
        <span className="muted small">
          Not enough bets to grade calibration
        </span>
      </div>
    );
  }
  return (
    <svg
      width={width}
      height={height}
      role="img"
      aria-label="Calibration"
      className="results-chart-svg"
    >
      {[0, 0.25, 0.5, 0.75, 1].map((t) => (
        <g key={t}>
          <line
            x1={x(t)}
            x2={x(t)}
            y1={y(0)}
            y2={y(1)}
            stroke="#2a2c3f"
            strokeDasharray="2 4"
          />
          <line
            x1={x(0)}
            x2={x(1)}
            y1={y(t)}
            y2={y(t)}
            stroke="#2a2c3f"
            strokeDasharray="2 4"
          />
          <text
            x={x(t)}
            y={y(0) + 12}
            fontSize="9"
            textAnchor="middle"
            fill="#6b7280"
          >
            {(t * 100).toFixed(0)}%
          </text>
          <text
            x={x(0) - 4}
            y={y(t) + 3}
            fontSize="9"
            textAnchor="end"
            fill="#6b7280"
          >
            {(t * 100).toFixed(0)}%
          </text>
        </g>
      ))}
      <line
        x1={x(0)}
        y1={y(0)}
        x2={x(1)}
        y2={y(1)}
        stroke="#4a4c68"
        strokeWidth={1}
        strokeDasharray="4 3"
      />
      {models.map((m) => {
        const buckets = m.calibration ?? [];
        if (!buckets.length) return null;
        const sorted = [...buckets].sort((a, b) => a.predicted - b.predicted);
        const line = sorted
          .map(
            (b) => `${x(b.predicted).toFixed(1)},${y(b.observed).toFixed(1)}`,
          )
          .join(" ");
        return (
          <g key={m.id}>
            <polyline
              points={line}
              fill="none"
              stroke={modelColor(m.id)}
              strokeWidth={1.5}
              strokeLinejoin="round"
              strokeLinecap="round"
              opacity={0.85}
            />
            {sorted.map((b, i) => (
              <circle
                key={i}
                cx={x(b.predicted)}
                cy={y(b.observed)}
                r={Math.max(2, Math.min(5, Math.sqrt(b.n) * 1.2))}
                fill={modelColor(m.id)}
                opacity={0.85}
              />
            ))}
          </g>
        );
      })}
    </svg>
  );
}

function ChartLegend({ models }: { models: ModelPerformance[] }) {
  return (
    <div className="results-chart-legend">
      {models.map((m) => (
        <span key={m.id} className="results-legend-item">
          <span
            className="results-legend-dot"
            style={{ background: modelColor(m.id) }}
          />
          {m.label}
        </span>
      ))}
    </div>
  );
}

function ChartsPanel({ models }: { models: ModelPerformance[] }) {
  const [open, setOpen] = useState<boolean>(true);
  if (!models.length) return null;
  return (
    <section className="results-charts-panel">
      <header className="results-charts-header">
        <button
          type="button"
          className="results-charts-toggle"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <span>Charts</span>
          <span className="results-charts-chev" aria-hidden>
            ▾
          </span>
        </button>
        {open && <ChartLegend models={models} />}
      </header>
      {open && (
        <div className="results-charts-grid">
          {CHART_SPECS.map((spec) => (
            <figure key={spec.key} className="results-chart-card">
              <figcaption>
                <strong>{spec.label}</strong>
                <span className="muted small">
                  {spec.dir === "lower"
                    ? "lower is better"
                    : "higher is better"}
                </span>
              </figcaption>
              <MultiLineChart models={models} spec={spec} />
            </figure>
          ))}
          <figure className="results-chart-card results-chart-card-wide">
            <figcaption>
              <strong>Calibration</strong>
              <span className="muted small">
                predicted vs. observed by probability bucket
              </span>
            </figcaption>
            <CalibrationChart models={models} />
          </figure>
        </div>
      )}
    </section>
  );
}

type PrimaryMetric = "logLoss" | "brier" | "pickAcc" | "roi";

const METRIC_META: Record<
  PrimaryMetric,
  {
    label: string;
    dir: "lower" | "higher";
    series: keyof ModelPerformance["series"];
  }
> = {
  logLoss: { label: "Log Loss", dir: "lower", series: "cum_log_loss" },
  brier: { label: "Brier", dir: "lower", series: "cum_brier" },
  pickAcc: { label: "Pick Acc", dir: "higher", series: "cum_pick_acc" },
  roi: { label: "ROI", dir: "higher", series: "cum_roi" },
};

function primaryValue(
  m: ModelPerformance,
  metric: PrimaryMetric,
): number | null {
  return m.metrics[metric];
}

function fmtPrimary(metric: PrimaryMetric, v: number | null): string {
  if (v == null) return "—";
  if (metric === "pickAcc") return fmtPct(v);
  if (metric === "roi") return fmtROI(v);
  return fmtNum(v);
}

function StandingsCard({
  model,
  rank,
  isLeader,
  primaryMetric,
  baselineValue,
}: {
  model: ModelPerformance;
  rank: number;
  isLeader: boolean;
  primaryMetric: PrimaryMetric;
  baselineValue: number | null;
}) {
  const own = primaryValue(model, primaryMetric);
  const direction = METRIC_META[primaryMetric].dir;
  let deltaEl: React.ReactNode = null;
  if (model.id !== "baseline" && own != null && baselineValue != null) {
    const delta = own - baselineValue;
    const improved = direction === "lower" ? delta < 0 : delta > 0;
    const absDelta = Math.abs(delta);
    const isPctScale = primaryMetric === "pickAcc" || primaryMetric === "roi";
    const shown = isPctScale
      ? `${(absDelta * 100).toFixed(1)}pp`
      : absDelta.toFixed(3);
    deltaEl = (
      <span
        className={
          improved ? "results-delta improved" : "results-delta regressed"
        }
      >
        {improved ? "↓" : "↑"}
        {shown}
      </span>
    );
  }
  const seriesKey = METRIC_META[primaryMetric].series;
  return (
    <div
      className={`results-standings-card${isLeader ? " is-leader" : ""}`}
      style={{ borderLeftColor: modelColor(model.id) }}
    >
      <div className="results-standings-header">
        <span className="results-rank">{rank}</span>
        <span className="results-model-label">{model.label}</span>
        <span className="results-rank-pill">
          {rank === 1
            ? "1st"
            : rank === 2
              ? "2nd"
              : rank === 3
                ? "3rd"
                : `${rank}th`}
        </span>
      </div>
      <div className="results-standings-body">
        <div>
          <div className="results-metric-label">
            {METRIC_META[primaryMetric].label}
          </div>
          <div className="results-metric-value">
            <span>{fmtPrimary(primaryMetric, own)}</span>
            {deltaEl}
          </div>
        </div>
        <Sparkline
          data={model.series[seriesKey] ?? []}
          color={modelColor(model.id)}
        />
      </div>
      <div className="results-standings-stats">
        <span>
          Acc <strong>{fmtPct(model.metrics.pickAcc)}</strong>
        </span>
        <span>
          ROI{" "}
          <strong
            className={
              model.metrics.roi != null && model.metrics.roi > 0
                ? "good"
                : model.metrics.roi != null && model.metrics.roi < 0
                  ? "bad"
                  : undefined
            }
          >
            {fmtROI(model.metrics.roi)}
          </strong>
        </span>
        <span>
          Bets <strong>{fmtRecord(model.metrics.bets)}</strong>
        </span>
      </div>
    </div>
  );
}

function SeasonTally({
  gamesGraded,
  betsPlaced,
  models,
}: {
  gamesGraded: number;
  betsPlaced: number;
  models: ModelPerformance[];
}) {
  const bestRoi = models
    .filter((m) => m.metrics.roi != null)
    .sort((a, b) => (b.metrics.roi ?? 0) - (a.metrics.roi ?? 0))[0];
  const worstBrier = models
    .filter((m) => m.metrics.brier != null)
    .sort((a, b) => (b.metrics.brier ?? 0) - (a.metrics.brier ?? 0))[0];
  return (
    <div className="results-tally">
      <div className="results-tally-title">Season tally</div>
      <div className="results-tally-grid">
        <div>
          <div className="results-tally-label">Games graded</div>
          <div className="results-tally-value">{gamesGraded}</div>
        </div>
        <div>
          <div className="results-tally-label">Bets placed</div>
          <div className="results-tally-value">{betsPlaced}</div>
        </div>
        <div>
          <div className="results-tally-label">Best ROI</div>
          <div className="results-tally-value good">
            {bestRoi ? fmtROI(bestRoi.metrics.roi) : "—"}
          </div>
          {bestRoi && <div className="results-tally-sub">{bestRoi.label}</div>}
        </div>
        <div>
          <div className="results-tally-label">Worst Brier</div>
          <div className="results-tally-value">
            {worstBrier ? fmtNum(worstBrier.metrics.brier) : "—"}
          </div>
          {worstBrier && (
            <div className="results-tally-sub">{worstBrier.label}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function GameCard({
  game,
  models,
}: {
  game: GradedGame;
  models: ModelPerformance[];
}) {
  const picks = Object.values(game.picks);
  const allCorrect =
    picks.length > 0 && picks.every((p) => p.correct === picks[0].correct);
  const allSamePick =
    picks.length > 0 && picks.every((p) => p.pick === picks[0].pick);
  const bets = picks.filter((p) => p.bet != null);
  const bestBet = bets
    .map((p) => p.bet!)
    .sort((a, b) => b.profit - a.profit)[0];
  const winSide = game.winner;
  const homeWon = winSide === "home";

  return (
    <div className="results-game-card">
      <div className="results-game-meta">
        {allSamePick ? (
          <span className="results-tag results-tag-consensus">CONSENSUS</span>
        ) : (
          <span className="results-tag results-tag-split">SPLIT</span>
        )}
        {bestBet && (
          <span className="results-best-bet">
            Best bet:{" "}
            <strong
              className={
                bestBet.profit > 0
                  ? "good"
                  : bestBet.profit < 0
                    ? "bad"
                    : undefined
              }
            >
              {bestBet.profit > 0 ? "+" : ""}
              {bestBet.profit.toFixed(2)}u
            </strong>
          </span>
        )}
      </div>
      <div className="results-game-teams">
        <div className={`results-team${homeWon ? "" : " winner"}`}>
          <span>{game.away_team_id}</span>
        </div>
        <div className={`results-team${homeWon ? " winner" : ""}`}>
          <span>@ {game.home_team_id}</span>
        </div>
      </div>
      <div className="results-game-picks">
        {models.map((m) => {
          const pick = game.picks[m.id];
          if (!pick) {
            return (
              <div
                key={m.id}
                className="results-pick"
                style={{ borderLeftColor: modelColor(m.id) }}
              >
                <div className="results-pick-header">
                  <span style={{ color: modelColor(m.id) }}>
                    {shortLabel(m.id)}
                  </span>
                  <span className="muted small">—</span>
                </div>
                <div className="muted small">no snapshot</div>
              </div>
            );
          }
          return (
            <div
              key={m.id}
              className={`results-pick${pick.bet ? " has-bet" : ""}`}
              style={{ borderLeftColor: modelColor(m.id) }}
            >
              <div className="results-pick-header">
                <span style={{ color: modelColor(m.id) }}>
                  {shortLabel(m.id)}
                </span>
                <span
                  className={`results-pick-chip ${pick.correct ? "correct" : "wrong"}`}
                  title={pick.correct ? "Pick was correct" : "Pick was wrong"}
                >
                  {pick.correct ? "✓" : "✗"}
                </span>
              </div>
              <div className="results-pick-body">
                <span className="muted">Picked</span>{" "}
                <strong>{pick.pick}</strong>{" "}
                <span className="muted small">
                  {(pick.prob * 100).toFixed(0)}%
                </span>
              </div>
              {pick.bet ? (
                <div
                  className={`results-pick-bet ${
                    pick.bet.outcome === "W"
                      ? "won"
                      : pick.bet.outcome === "L"
                        ? "lost"
                        : "push"
                  }`}
                >
                  {pick.bet.outcome ?? "—"}{" "}
                  {pick.bet.profit !== 0
                    ? `${pick.bet.profit > 0 ? "+" : ""}${pick.bet.profit.toFixed(2)}u`
                    : ""}
                </div>
              ) : (
                <div className="results-pick-nobet">no EV+ bet</div>
              )}
            </div>
          );
        })}
      </div>
      {allCorrect ? (
        <span className="muted small">
          All {picks.length} models {picks[0]?.correct ? "correct" : "wrong"}
        </span>
      ) : null}
    </div>
  );
}

function shortLabel(modelId: string): string {
  const map: Record<string, string> = {
    baseline: "Base",
    dvoa_box_impact: "Box",
    epm_style: "EPM",
    user: "Yours",
  };
  return map[modelId] ?? modelId;
}

export function ResultsView() {
  const { user } = useAuth();
  const [data, setData] = useState<ResultsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [windowSel, setWindowSel] = useState<ResultsWindow>("std");
  const [primaryMetric, setPrimaryMetric] = useState<PrimaryMetric>("logLoss");
  const [teamQ, setTeamQ] = useState<string>("");
  const [evOnly, setEvOnly] = useState<boolean>(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    queueMicrotask(() => {
      setLoading(true);
      setError(null);
    });
    api
      .getResults({ window: windowSel, idToken: user.idToken })
      .then((res) => {
        if (cancelled) return;
        setData(res);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [windowSel, user]);

  const models = useMemo<ModelPerformance[]>(() => data?.models ?? [], [data]);
  const ranked = useMemo(() => {
    const dir = METRIC_META[primaryMetric].dir;
    return [...models].sort((a, b) => {
      const av = a.metrics[primaryMetric];
      const bv = b.metrics[primaryMetric];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return dir === "lower" ? av - bv : bv - av;
    });
  }, [models, primaryMetric]);

  const baselineValue = useMemo(
    () =>
      models.find((m) => m.id === "baseline")?.metrics[primaryMetric] ?? null,
    [models, primaryMetric],
  );

  const visibleGames = useMemo(() => {
    if (!data) return [];
    let games = data.games;
    const q = teamQ.trim().toLowerCase();
    if (q) {
      games = games.filter(
        (g) =>
          g.home_team_id.toLowerCase().includes(q) ||
          g.away_team_id.toLowerCase().includes(q),
      );
    }
    if (evOnly) {
      games = games.filter((g) =>
        Object.values(g.picks).some((p) => p.bet != null),
      );
    }
    return games;
  }, [data, teamQ, evOnly]);

  const gamesByDate = useMemo(() => {
    const buckets = new Map<string, GradedGame[]>();
    for (const g of visibleGames) {
      const key = g.date;
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key)!.push(g);
    }
    return [...buckets.entries()].sort(([a], [b]) => b.localeCompare(a));
  }, [visibleGames]);

  if (!user) {
    return (
      <div className="results-view">
        <p className="muted">Sign in to view prediction results and scoring.</p>
      </div>
    );
  }

  return (
    <div className="results-view">
      <div className="results-header">
        <div className="results-header-meta">
          {data && (
            <>
              <span>{data.season_tally.games_graded} games graded</span>
              <span className="muted">·</span>
              <span>{data.season_tally.bets_placed} bets placed</span>
            </>
          )}
        </div>
        <div className="results-window-toggle">
          <span className="muted small">Window</span>
          {(["std", "d30", "d7"] as const).map((w) => (
            <button
              key={w}
              type="button"
              className={`results-pill${w === windowSel ? " active" : ""}`}
              onClick={() => setWindowSel(w)}
            >
              {w === "std" ? "Season" : w === "d30" ? "30d" : "7d"}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="muted small">Loading results…</div>}
      {error && (
        <div className="results-error">Failed to load results: {error}</div>
      )}

      {!loading && !error && data && data.models.length === 0 && (
        <div className="results-empty">
          <strong>No graded games yet.</strong>
          <p className="muted small">
            Results accumulate here as games finalize. The score job snapshots
            each game's prediction at close time so accuracy is computed without
            lookahead bias.
          </p>
        </div>
      )}

      {!loading && !error && data && data.models.length > 0 && (
        <div className="results-layout">
          <aside className="results-rail">
            <div className="results-standings">
              <div className="results-standings-title">
                <div>Standings</div>
                <div className="muted small">
                  Ranked by {METRIC_META[primaryMetric].label.toLowerCase()} ·{" "}
                  {METRIC_META[primaryMetric].dir === "lower"
                    ? "lower is better"
                    : "higher is better"}
                </div>
              </div>
              <div className="results-rank-by">
                {(Object.keys(METRIC_META) as PrimaryMetric[]).map((k) => (
                  <button
                    key={k}
                    type="button"
                    className={`results-pill small${k === primaryMetric ? " active" : ""}`}
                    onClick={() => setPrimaryMetric(k)}
                  >
                    {METRIC_META[k].label}
                  </button>
                ))}
              </div>
              {ranked.map((m, i) => (
                <StandingsCard
                  key={m.id}
                  model={m}
                  rank={i + 1}
                  isLeader={i === 0}
                  primaryMetric={primaryMetric}
                  baselineValue={baselineValue}
                />
              ))}
            </div>
            <SeasonTally
              gamesGraded={data.season_tally.games_graded}
              betsPlaced={data.season_tally.bets_placed}
              models={models}
            />
          </aside>
          <section className="results-content">
            <ChartsPanel models={ranked} />
            <div className="results-filterbar">
              <input
                type="search"
                placeholder="Filter by team…"
                value={teamQ}
                onChange={(e) => setTeamQ(e.target.value)}
                className="results-search"
              />
              <label className="results-check">
                <input
                  type="checkbox"
                  checked={evOnly}
                  onChange={(e) => setEvOnly(e.target.checked)}
                />
                EV+ bets only
              </label>
              <div className="muted small" style={{ marginLeft: "auto" }}>
                Showing {visibleGames.length} of {data.games.length} graded
              </div>
            </div>
            <div className="results-games">
              {gamesByDate.length === 0 ? (
                <div className="results-empty">
                  <p className="muted small">
                    No graded games match your filters.
                  </p>
                </div>
              ) : (
                gamesByDate.map(([date, games]) => (
                  <Fragment key={date}>
                    <div className="results-date-header">
                      <span>{fmtGameDate(date)}</span>
                      <span className="muted small">
                        {games.length} game{games.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                    {games.map((g) => (
                      <GameCard
                        key={g.league_game_id}
                        game={g}
                        models={ranked}
                      />
                    ))}
                  </Fragment>
                ))
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
